import React, { useState, useEffect, useCallback, useRef } from "react"
import ListBox from "./ListBox"
import IndicatorsContainer from "./IndicatorsContainer"
import InputTextContainer from "./InputDefaultTextContainer"
import '../../../styles/common/core/DropDownSelect.css'
import { isNullOrEmpty } from "../../../shared/scripts"

const dropDownInputState = {
  unFocused: "unFocused",
  unFocusedByOutsideClick: "unFocusedByOutsideClick",
  focused: "focused",
};

const DropDownSelect = ({
  optionsList = [],
  itemTitleFactory = undefined, //  or function
  hasOptionAttentionFunc = undefined, // or function
  isClearable = false,
  validate = false,
  disabled = false,
  className = "",
  defaultText = "Select...",
  name = null,
  value = "",
  width = 250,
  minWidth = null,
  maxWidth = null,
  onSelect = null,
  fullWidth = false,
  readOnly = false,
}) => {
  const _isNullval = value == null;
  const _val = _isNullval ? "" : getLabelByValue(value, optionsList);
  const [inputText, setInputText] = useState(_val);
  const [filterText, setFilterText] = useState("");
  const [selectLabel, setSelectLabel] = useState(_val);
  const [selectValue, setSelectValue] = useState(_isNullval ? "" : value);
  const [showListBox, setShowListBox] = useState(false);
  const thisRef = useRef(null);
  const inputRef = useRef(null);
  const inputStateRef = useRef(dropDownInputState.unFocused);

  const setInputStateInner = useCallback(value => {
    inputStateRef.current = value;
  }, [inputStateRef]);

  useEffect(() => {
    if (value == null) {
      setInputText("");
      setFilterText("");
      setSelectValue("");
      setSelectLabel("");
    } else if (value !== ""
      && value.toString().toLowerCase() !== selectValue.toString().toLowerCase()
    ) {
      setSelectValue(value);
      setSelectLabel(getLabelByValue(value, optionsList));
      setInputText(getLabelByValue(value, optionsList));

      const firstFound = getFirstItemFromOptions(value, optionsList);
      if (firstFound != null) {
        setInputText(firstFound.label);
        setSelectLabel(firstFound.label);
        setSelectValue(firstFound.value);
      }
    }
  }, [value, selectValue, optionsList]);

  function getLabelByValue(value, optionsList) {
    const valueStr = value.toString();
    const optionPair = optionsList.find(option => option.value === valueStr);

    return optionPair?.label ?? valueStr;
  }

  function getFirstItemFromOptions(searchLabel, optionsList, containsSearch = false) {
    const searchLabelLower = searchLabel.toString().toLowerCase();
    if (containsSearch) {
      return optionsList.find(item => item.label.toString().toLowerCase().includes(searchLabelLower));
    }

    const resultByLabels = optionsList.find(item => item.label.toString().toLowerCase() == searchLabelLower);
    return resultByLabels == null
      ? optionsList.find(item => item.value.toString().toLowerCase() == searchLabelLower)
      : null;
  }

  const setDefaults = () => {
    setFilterText("");
    setInputText("");
    setSelectLabel("");
    setSelectValue("");

    if (onSelect != null) {
      onSelect("");
    }
  };

  function getWidthStyles() {
    if (fullWidth) {
      return {
        width: "100%"
      }
    }

    const style = {
      width: width + "px"
    };
    if (minWidth !== null) {
      style["minWidth"] = minWidth + "px";
    }
    if (maxWidth !== null) {
      style["maxWidth"] = maxWidth + "px";
    }

    return style;
  }


  /*** Handle methods */
  const handleInputBlur = event => {
    setInputStateInner(dropDownInputState.unFocused);

    setShowListBox(false);
    setInputText(selectLabel);
    setFilterText("");
  }

  const handleInputFocus = () => {
    setInputStateInner(dropDownInputState.focused);
    setShowListBox(true);
  }

  const handleAngleIconMouseDown = useCallback(event => {
    event.preventDefault();

    const nextState = inputStateRef.current === dropDownInputState.unFocused
      ? dropDownInputState.focused
      : dropDownInputState.unFocused;

    if (nextState === dropDownInputState.unFocused) {
      inputRef.current.blur();  //  see: handleInputBlur
    } else if (nextState === dropDownInputState.focused) {
      inputRef.current.focus(); //  see: handleInputFocus
    }
  }, []);

  const handleListItemClick = indexObjPair => {
    function tryGetOption() {
      try {
        const realObj = optionsList[indexObjPair.index];
        if (indexObjPair.label === realObj.label) {
          return { bool: true, obj: realObj };
        }
      } catch (error) { };

      return { bool: false, obj: null };
    }

    const boolObjPair = tryGetOption();
    if (boolObjPair.bool) {
      setInputText(boolObjPair.obj.label);
      setFilterText("");

      const item = boolObjPair.obj;
      setSelectLabel(item.label);
      setSelectValue(item.value);

      if (onSelect != null) {
        onSelect(item.value);
      }

      return;
    }

    setDefaults();
  }

  const handleInputChange = inputValue => {
    const newVal = isNullOrEmpty(inputValue)
      ? inputValue
      : inputValue.replace(/[~`!@#$%^&*()+={}\[\];:\'\"<>\.,\/\\\?\-\_]/g, '');

    setInputText(newVal);
    setFilterText(newVal);
  }

  const handleInputKeyEnterClick = () => {
    if (inputText === "") {
      setDefaults();

      return;
    }

    setFilterText("");
    const firstFound = getFirstItemFromOptions(inputText, optionsList, true);
    if (firstFound == null) {
      return;
    }

    inputRef.current.blur();
    setSelectLabel(firstFound.label);
    setSelectValue(firstFound.value);
    setInputText(firstFound.label);
    if (onSelect != null) {
      onSelect(firstFound.value);
    }
  }

  const handleInputKeyEscapeClick = () => {
    inputRef.current.blur();
  }


  return (
    <div
      style={getWidthStyles()}
      className={"dropdown-select" + (className === "" ? "" : " " + className)}
      ref={thisRef}
      data-disabled={disabled}
      data-readonly={readOnly}
      data-name={name}
    >
      <div className="input-indicator-container">
        <InputTextContainer
          isDisabled={disabled}
          isReadOnly={readOnly}
          inputRef={inputRef}
          value={inputText}
          defaultText={defaultText}
          validate={validate}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyEnter={handleInputKeyEnterClick}
          onKeyEscape={handleInputKeyEscapeClick}
        />
        <IndicatorsContainer
          isDisabled={disabled || readOnly}
          clear={isClearable}
          onClearMouseDown={setDefaults}
          angle
          onAngleMouseDown={handleAngleIconMouseDown}
          angleRotate={showListBox}
        />
      </div>
      {!(disabled || readOnly) &&
        <ListBox
          isShow={showListBox}
          dataList={optionsList}
          filterValue={filterText}
          selectLabel={selectLabel}
          onListItemClick={handleListItemClick}
          hasItemAttentionFunc={hasOptionAttentionFunc}
          itemTitleFactory={itemTitleFactory}
        />
      }
    </div>
  );
};


export default DropDownSelect;